/* ... existing imports ... */
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiX, FiSave, FiAlertCircle, FiMessageSquare } = FiIcons

const TradeForm = ({ trade, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    symbol: '',
    side: 'buy',
    entry_price: '',
    exit_price: '',
    quantity: '',
    entry_time: '',
    setup_type: '',
    notes: '',
    pnl: ''
  })
  const [error, setError] = useState('')

  useEffect(() => {
    if (trade) {
      setFormData({
        symbol: trade.symbol || '',
        side: trade.side || 'buy',
        entry_price: trade.entry_price?.toString() || '',
        exit_price: trade.exit_price?.toString() || '',
        quantity: trade.quantity?.toString() || '',
        entry_time: trade.entry_time ? new Date(trade.entry_time).toISOString().slice(0, 16) : '',
        setup_type: trade.setup_type || '',
        notes: trade.notes || '',
        pnl: trade.pnl?.toString() || ''
      })
    }
  }, [trade])

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto"
    >
      <motion.div 
        initial={{ scale: 0.9, y: 20 }} 
        animate={{ scale: 1, y: 0 }} 
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full my-8 border border-gray-200 dark:border-slate-700"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{trade ? 'Edit Trade' : 'Add New Trade'}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="h-6 w-6 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Symbol</label>
                <input 
                  type="text" 
                  value={formData.symbol} 
                  onChange={(e) => handleChange('symbol', e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                  placeholder="e.g. BTC/USDT"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Side</label>
                <select 
                  value={formData.side} 
                  onChange={(e) => handleChange('side', e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                >
                  <option value="buy">Buy / Long</option>
                  <option value="sell">Sell / Short</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['entry_price', 'exit_price', 'quantity'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    {field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </label>
                  <input 
                    type="number" 
                    step="any"
                    value={formData[field]} 
                    onChange={(e) => handleChange(field, e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Notes</label>
              <textarea 
                value={formData.notes} 
                onChange={(e) => handleChange('notes', e.target.value)}
                rows="3"
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white resize-none"
                placeholder="Trade rationale..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t dark:border-slate-700">
              <button 
                type="button" 
                onClick={onClose} 
                className="px-6 py-2.5 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="flex items-center space-x-2 px-8 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
              >
                <SafeIcon icon={FiSave} className="h-5 w-5" />
                <span>{trade ? 'Update' : 'Save'} Trade</span>
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default TradeForm